import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  MAX_BULK_ROSTER_SELECTION,
  campaignCopy,
  errorMessage,
  selectIsReadOnlyAccess,
  useBulkApproveCreators,
  useBulkDeleteCreators,
  useCampaignCapabilitiesStore,
} from '@discovr/features';
import type { BulkApproveItem, BulkApproveSelectedResult, BulkDeleteSelectedResult } from '@discovr/features';
import type { RowSelectionConfig } from '../../../components/common/DataTable';
import { rowDisplayName } from '../../utils/rowDisplayName';
import { BulkApproveDialog } from './BulkApproveDialog';
import { BulkDeleteDialog } from './BulkDeleteDialog';
import {
  BULK_APPROVED_STATUS,
  BULK_APPROVE_MODE,
  applySelectionChange,
  capSelection,
  describeBlockingCreators,
  hasBulkActions,
  idsGoneFromPage,
  idsMissingCommercial,
  idsNotOfferable,
  isRowBulkSelectable,
  reconcileSelection,
  resolveBulkCapabilities,
  selectedAmong,
  summarizeBulkResult,
  variantNeedsDeliverables,
  withoutIds,
  type BulkApproveVariant,
  type BulkPermissions,
  type BulkResultSummary,
  type PageSnapshot,
} from './bulkSelection';
import type { CreatorsRow, CreatorsSubTab } from './types';

type BulkDialog = 'approve' | 'delete' | null;
type Selection = ReadonlyMap<string, CreatorsRow>;

const EMPTY_SELECTION: Selection = new Map();
const EMPTY_IDS: ReadonlySet<string> = new Set();
const DEFAULT_APPROVE_VARIANT: BulkApproveVariant = 'brand-approve';

const APPROVE_SELECTED_LABEL: Record<BulkApproveVariant, string> = {
  'brand-approve': 'Approve selected',
  'approve-as-brand': 'Approve selected as brand',
  'admin-approve': 'Approve selected',
};

function approveSelectedLabelFor(variant: BulkApproveVariant | null): string | null {
  if (!variant) return null;
  return APPROVE_SELECTED_LABEL[variant];
}

/** Brand approval (and the admin proxy) moves rows off Pending; admin final
 *  approval keeps them on Approved (accepted_by_brand → accepted_by_admin). */
function approvalLeavesTab(variant: BulkApproveVariant): boolean {
  return variant !== 'admin-approve';
}

function showBulkToast(summary: BulkResultSummary) {
  const options = { description: summary.description };
  if (summary.tone === 'success') toast.success(summary.message, options);
  else if (summary.tone === 'warning') toast.warning(summary.message, options);
  else toast.error(summary.message, options);
}

function notifyCappedSelection(added: number) {
  if (added === 0) toast.warning(campaignCopy.creator.bulkSelection.tooMany(MAX_BULK_ROSTER_SELECTION));
  else toast.warning(campaignCopy.creator.bulkSelection.partlyAdded(added, MAX_BULK_ROSTER_SELECTION));
}

export interface UseCreatorsBulkActionsArgs {
  campaignId: string;
  subTab: CreatorsSubTab;
  isAdminViewer: boolean;
  isPublicView: boolean;
  permissions: BulkPermissions | null | undefined;
  /** Rows currently rendered by the table (one page). */
  pageRows: readonly CreatorsRow[];
  /** Identity of the query behind `pageRows` (sub-tab, page, sort, search). */
  pageKey: string;
  /** True while `pageRows` are the previous view's rows kept on screen until `pageKey`'s data loads. */
  pageIsPlaceholder?: boolean;
  /** Rows hidden by an optimistic row action; dropped from the selection. */
  hiddenRowIds: ReadonlySet<string>;
  platform: string;
  optimisticSelections: ReadonlyMap<string, string[]>;
  /** Called with ids that left the current sub-tab so the caller can hide them. */
  onRowsLeftTab: (ids: string[]) => void;
}

export interface CreatorsBulkActions {
  rowSelection: RowSelectionConfig<CreatorsRow> | undefined;
  selectedCount: number;
  /** Label for the approve button while a selection exists; null when the tab has no bulk approve. */
  approveSelectedLabel: string | null;
  /** True while the bulk approve request is in flight. */
  approvePending: boolean;
  /** Selected rows that need a commercial picked in the table; filled only after an approve attempt. */
  needsCommercialIds: ReadonlySet<string>;
  /** Selected rows whose profile can't be offered to the brand; filled only after an approve attempt. */
  unofferableIds: ReadonlySet<string>;
  canDeleteSelected: boolean;
  openApprove: () => void;
  openDelete: () => void;
  dialogs: ReactNode;
}

const noop = () => {};

const NO_BULK_ACTIONS: CreatorsBulkActions = {
  rowSelection: undefined,
  selectedCount: 0,
  approveSelectedLabel: null,
  approvePending: false,
  needsCommercialIds: EMPTY_IDS,
  unofferableIds: EMPTY_IDS,
  canDeleteSelected: false,
  openApprove: noop,
  openDelete: noop,
  dialogs: null,
};

/**
 * Row selection + bulk approve/delete for the Creators tab. Selected rows are
 * snapshotted so they survive pagination; the selection resets on sub-tab change.
 * Every approval confirms in a dialog. On Pending it is refused first while a
 * selected creator has no commercial in the table, or (approve-as-brand) a
 * profile that can't be offered to the brand.
 */
export function useCreatorsBulkActions({
  campaignId,
  subTab,
  isAdminViewer,
  isPublicView,
  permissions,
  pageRows,
  pageKey,
  pageIsPlaceholder = false,
  hiddenRowIds,
  platform,
  optimisticSelections,
  onRowsLeftTab,
}: UseCreatorsBulkActionsArgs): CreatorsBulkActions {
  const capabilities = useMemo(
    () => resolveBulkCapabilities(subTab, isAdminViewer, permissions, isPublicView),
    [subTab, isAdminViewer, permissions, isPublicView],
  );
  const approveVariant = capabilities.approveVariant ?? DEFAULT_APPROVE_VARIANT;
  const approvesFromTable = capabilities.approveVariant !== null && variantNeedsDeliverables(approveVariant);
  const checksOfferability = capabilities.approveVariant === 'approve-as-brand';
  const isReadOnly = useCampaignCapabilitiesStore(selectIsReadOnlyAccess);
  const approveMutation = useBulkApproveCreators(
    campaignId,
    BULK_APPROVE_MODE[approveVariant],
    BULK_APPROVED_STATUS[approveVariant],
  );
  const deleteMutation = useBulkDeleteCreators(campaignId);

  const [selection, setSelection] = useState<Selection>(EMPTY_SELECTION);
  const [dialog, setDialog] = useState<BulkDialog>(null);
  // Blockers are flagged only once an approval was refused, so ticking a row never looks like an error.
  const [flagBlockers, setFlagBlockers] = useState(false);
  const [trackedTab, setTrackedTab] = useState(subTab);
  if (trackedTab !== subTab) {
    setTrackedTab(subTab);
    setSelection(EMPTY_SELECTION);
    setDialog(null);
    setFlagBlockers(false);
  }

  // A missing row may have left the tab or just shifted page; only the server knows, so drop it and say so.
  // Placeholder rows belong to the previous view, not to pageKey, so they are neither compared nor kept.
  const lastPageRef = useRef<PageSnapshot | null>(null);
  useEffect(() => {
    const currentPage: PageSnapshot = { key: pageKey, ids: new Set(pageRows.map((row) => String(row.id))) };
    const goneIds = pageIsPlaceholder ? new Set<string>() : idsGoneFromPage(lastPageRef.current, currentPage);
    if (!pageIsPlaceholder && currentPage.ids.size > 0) lastPageRef.current = currentPage;
    const autoDeselected = selectedAmong(selection, goneIds).filter((id) => !hiddenRowIds.has(id));
    if (autoDeselected.length > 0) toast.info(campaignCopy.creator.bulkSelection.autoDeselected(autoDeselected.length));
    const droppedIds = new Set([...hiddenRowIds, ...goneIds]);
    setSelection((prev) => reconcileSelection(prev, pageRows, subTab, droppedIds));
  }, [pageRows, pageKey, pageIsPlaceholder, subTab, hiddenRowIds, selection]);

  const selectedIds = useMemo(() => new Set(selection.keys()), [selection]);
  const selectedRows = useMemo(() => [...selection.values()], [selection]);
  const nameById = useMemo(
    () => new Map(selectedRows.map((row) => [String(row.id), rowDisplayName(row)])),
    [selectedRows],
  );
  const needsCommercialIds = useMemo(
    () => (approvesFromTable ? idsMissingCommercial(selectedRows, optimisticSelections, platform) : EMPTY_IDS),
    [approvesFromTable, selectedRows, optimisticSelections, platform],
  );

  const unofferableIds = useMemo(
    () => (checksOfferability ? idsNotOfferable(selectedRows) : EMPTY_IDS),
    [checksOfferability, selectedRows],
  );

  const closeDialog = () => setDialog(null);

  // Emptying the selection closes both dialogs (they act on it) and clears the
  // blocker flags, so rows ticked next aren't pre-flagged.
  if (dialog !== null && selection.size === 0) setDialog(null);
  if (flagBlockers && selection.size === 0) setFlagBlockers(false);

  const handleSelectionChange = (nextIds: Set<string>) => {
    const capped = capSelection(selectedIds, nextIds, MAX_BULK_ROSTER_SELECTION);
    if (capped.truncated) notifyCappedSelection(capped.added);
    if (capped.truncated && capped.added === 0) return;
    setSelection((prev) => applySelectionChange(prev, capped.ids, pageRows));
  };

  const handleDeselect = (rowId: string) => {
    setSelection((prev) => withoutIds(prev, [rowId]));
  };

  const settleBatch = (appliedIds: string[], leavesTab: boolean) => {
    setSelection((prev) => withoutIds(prev, appliedIds));
    if (leavesTab && appliedIds.length > 0) onRowsLeftTab(appliedIds);
    closeDialog();
  };

  // Only the request sits in the try: a throw after the batch applied must not read as "Could not approve".
  const handleApproveConfirm = async (items: BulkApproveItem[]) => {
    let result: BulkApproveSelectedResult;
    try {
      result = await approveMutation.mutateAsync(items);
    } catch (err) {
      toast.error(errorMessage(err, campaignCopy.creator.bulkApprove.error));
      return;
    }
    showBulkToast(
      summarizeBulkResult(
        result.approved_count,
        result.failed,
        nameById,
        campaignCopy.creator.bulkApprove.pastVerb,
        campaignCopy.creator.bulkApprove.noneApproved,
      ),
    );
    settleBatch(result.approved_ids, approvalLeavesTab(approveVariant));
  };

  const refuseApproval = (message: string, blockingIds: ReadonlySet<string>) => {
    toast.warning(message, { description: describeBlockingCreators(blockingIds, nameById) });
    setFlagBlockers(true);
  };

  // The refusals run before the dialog opens, so a confirm can't be armed for rows the BE would reject.
  const requestApprove = () => {
    if (approveMutation.isPending || selectedRows.length === 0) return;
    // Both kinds are flagged in the table, so each gets its own toast naming its rows.
    if (needsCommercialIds.size > 0) {
      refuseApproval(campaignCopy.creator.bulkApprove.needsCommercial(needsCommercialIds.size), needsCommercialIds);
    }
    if (unofferableIds.size > 0) {
      refuseApproval(campaignCopy.creator.bulkApprove.notOfferable(unofferableIds.size), unofferableIds);
    }
    if (needsCommercialIds.size > 0 || unofferableIds.size > 0) return;
    setFlagBlockers(false);
    setDialog('approve');
  };

  // Like requestApprove, a delete already in flight is not started again.
  const requestDelete = () => {
    if (deleteMutation.isPending) return;
    setDialog('delete');
  };

  const handleDeleteConfirm = async (confirm: string) => {
    const requestedIds = selectedRows.map((row) => String(row.id));
    let result: BulkDeleteSelectedResult;
    try {
      result = await deleteMutation.mutateAsync({ entryIds: requestedIds, confirm });
    } catch (err) {
      toast.error(errorMessage(err, campaignCopy.creator.bulkDelete.error));
      return;
    }
    showBulkToast(
      summarizeBulkResult(
        result.deleted_count,
        result.failed,
        nameById,
        campaignCopy.creator.bulkDelete.pastVerb,
        campaignCopy.creator.bulkDelete.noneDeleted,
      ),
    );
    settleBatch(result.deleted_ids, true);
  };

  if (!hasBulkActions(capabilities)) return NO_BULK_ACTIONS;

  const rowSelection: RowSelectionConfig<CreatorsRow> = {
    selectedIds,
    onSelectionChange: handleSelectionChange,
    isRowSelectable: (row) => isRowBulkSelectable(row, subTab),
    getRowLabel: (row) => rowDisplayName(row),
  };

  const hasSelection = selection.size > 0;
  // Gated on read-only too, so a host passing level-unaware permissions still can't offer delete.
  const canDelete = capabilities.canDelete && !isReadOnly;

  const showApproveDialog = hasSelection && dialog === 'approve';
  const showDeleteDialog = hasSelection && dialog === 'delete' && canDelete;
  const dialogs = (
    <>
      {showApproveDialog && capabilities.approveVariant && (
        <BulkApproveDialog
          variant={capabilities.approveVariant}
          rows={selectedRows}
          platform={platform}
          optimisticSelections={optimisticSelections}
          busy={approveMutation.isPending}
          onClose={closeDialog}
          onDeselect={handleDeselect}
          onConfirm={handleApproveConfirm}
        />
      )}
      {showDeleteDialog && (
        <BulkDeleteDialog
          rows={selectedRows}
          platform={platform}
          busy={deleteMutation.isPending}
          onClose={closeDialog}
          onDeselect={handleDeselect}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );

  return {
    rowSelection,
    selectedCount: selection.size,
    approveSelectedLabel: isReadOnly ? null : approveSelectedLabelFor(capabilities.approveVariant),
    approvePending: approveMutation.isPending,
    needsCommercialIds: flagBlockers ? needsCommercialIds : EMPTY_IDS,
    unofferableIds: flagBlockers ? unofferableIds : EMPTY_IDS,
    canDeleteSelected: canDelete,
    openApprove: requestApprove,
    openDelete: requestDelete,
    dialogs,
  };
}
